import { Suspense, useCallback, useRef, type FC } from 'react';
import { fetchQuery, useQueryLoader, useRelayEnvironment, graphql } from 'react-relay';

import { FormattedMessage } from 'dibs-react-intl';

import { getConsentedBuyerId, getGuestId } from 'dibs-cookie-jar';
import { useClientState } from 'dibs-react-hooks/exports/useClientState.ts';
import { dibsLazyClient } from 'dibs-lazy/exports/dibsLazyClient.ts';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker.tsx';
import { filterFalsy } from 'dibs-ts-utils/exports/filterFalsy.ts';
import { getUserSessionCountryCode } from 'dibs-regional-info/exports/regionalInfoHelpers.ts';

import { useHomePageContext } from '../HomePageContext.tsx';
import { buildItemsGridDataQuery } from './buildItemsGridData.ts';
import { type HpSharedItemsGridTrendingSeedQuery } from './__generated__/HpSharedItemsGridTrendingSeedQuery.graphql.ts';
import { getRecentlyViewedItemIds } from './getRecentlyViewedItemIds.ts';
import { getHpSharedItemsGridInitialFetchCounts } from './constants.ts';
import { HpSharedItemsGridShimmer } from './HpSharedItemsGridShimmer.tsx';
import { type buildItemsGridDataQuery as BuildItemsGridDataQuery } from './__generated__/buildItemsGridDataQuery.graphql.ts';

import dibsCss from 'dibs-css';
import classnames from 'classnames';

const HpSharedItemsGridContent = dibsLazyClient(
    () =>
        import(/* webpackChunkName: "HpSharedItemsGridContent" */ './HpSharedItemsGridContent.tsx')
);

type HpSharedItemsGridProps = {
    userId: string | null;
};

export const HpSharedItemsGrid: FC<HpSharedItemsGridProps> = ({ userId }) => {
    const [queryRef, loadQuery] = useQueryLoader<BuildItemsGridDataQuery>(buildItemsGridDataQuery);
    const environment = useRelayEnvironment();
    const gridRef = useRef(null);
    const { priceBookName, isTrade, userType, isMobile } = useHomePageContext();
    const isClient = useClientState();
    const hasLoadedRef = useRef(false);

    const loadGridQuery = useCallback(async (): Promise<void> => {
        if (hasLoadedRef.current) {
            return;
        }
        hasLoadedRef.current = true;

        let recentlyViewedItemIds = getRecentlyViewedItemIds();

        if (recentlyViewedItemIds.length < 4) {
            const trendingResult = await fetchQuery<HpSharedItemsGridTrendingSeedQuery>(
                environment,
                graphql`
                    query HpSharedItemsGridTrendingSeedQuery {
                        viewer {
                            itemSearchTrendingItems(
                                sellerVerticals: ["jewelry", "furniture", "fashion", "art"]
                            ) {
                                itemIds
                            }
                        }
                    }
                `,
                {},
                { fetchPolicy: 'store-or-network' }
            ).toPromise();
            recentlyViewedItemIds =
                trendingResult?.viewer?.itemSearchTrendingItems?.itemIds?.filter(filterFalsy) || [];
        }
        loadQuery({
            userCountryCode: getUserSessionCountryCode(),
            priceBookName,
            isTrade,
            consentedBuyerId: getConsentedBuyerId(document.cookie),
            guestId: getGuestId(document.cookie),
            recentlyViewedItemIds,
            homepageId: '',
            userType,
            abTestList: [],
            ...getHpSharedItemsGridInitialFetchCounts(isMobile),
        });
    }, [environment, isMobile, isTrade, loadQuery, priceBookName, userType]);

    return (
        <div ref={gridRef}>
            {isClient ? (
                <VisibilityTracker
                    elementRef={gridRef}
                    onVisibilityChange={({ isVisible }) => {
                        if (isVisible) {
                            void loadGridQuery();
                        }
                    }}
                    observerOptions={{
                        rootMargin: '100% 0px',
                    }}
                />
            ) : null}
            <div
                className={classnames(dibsCss.mbLarge, dibsCss.mtLarger, dibsCss.textCenter, {
                    [dibsCss.sassyFontHeaderLarge]: isMobile,
                    [dibsCss.sassyFontHeaderXxLarge]: !isMobile,
                })}
            >
                <FormattedMessage
                    id="abm.HpSharedItemsGrid.title"
                    defaultMessage="Curated <italicWrapper>Just for You</italicWrapper>"
                    values={{
                        italicWrapper: chunks => (
                            <span
                                className={
                                    isMobile
                                        ? dibsCss.sassyFontHeaderLargeItalic
                                        : dibsCss.sassyFontHeaderXxLargeItalic
                                }
                            >
                                {chunks}
                            </span>
                        ),
                    }}
                />
            </div>
            {!queryRef ? <HpSharedItemsGridShimmer /> : null}
            {queryRef ? (
                <Suspense fallback={<HpSharedItemsGridShimmer />}>
                    <HpSharedItemsGridContent queryRef={queryRef} userId={userId} />
                </Suspense>
            ) : null}
        </div>
    );
};
